import type { Product } from "schemas/generated/product";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { formatPrice } from "replo-utils/lib/math";

const STATUS_CONFIGS = {
  active: {
    iconClass: "bg-positive-default",
    badgeClass: "bg-success-soft text-success-default",
  },
  draft: {
    iconClass: "bg-warning-default",
    badgeClass: "bg-warning-soft text-warning-default",
  },
  archived: {
    iconClass: "bg-component-subtle",
    badgeClass: "bg-component-subtle text-foreground-muted",
  },
} as const;

export const getStatusIcon = (status: Product["status"]) => {
  const config = STATUS_CONFIGS[status as keyof typeof STATUS_CONFIGS];
  const iconClass = config?.iconClass ?? "bg-component";
  return <div className={twMerge("w-2 h-2 rounded-full", iconClass)} />;
};

export const getStatusBadgeClasses = (status: Product["status"]): string => {
  const config = STATUS_CONFIGS[status as keyof typeof STATUS_CONFIGS];
  return config?.badgeClass ?? "bg-component text-foreground";
};

const formatPriceRange = (minPrice: number, maxPrice: number): string => {
  return minPrice === maxPrice
    ? formatPrice(minPrice)
    : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
};

export const getPriceRange = (variants: Product["variants"]) => {
  if (!variants?.length) {
    return "No price";
  }

  const validPrices = variants
    .map((v) => v.price)
    .filter((price): price is number => typeof price === "number");

  if (!validPrices.length) {
    return "No price";
  }

  const minPrice = Math.min(...validPrices);
  const maxPrice = Math.max(...validPrices);

  return formatPriceRange(minPrice, maxPrice);
};

export const mediaToImageUrls = (
  media: Product["options"][number]["optionValues"][number]["media"],
): string[] => {
  return (
    media
      ?.map((mediaItem) => {
        if (mediaItem.type === "image") {
          return mediaItem.url;
        } else if (mediaItem.type === "video") {
          return mediaItem.previewImage?.url ?? "";
        } else if (mediaItem.type === "externalVideo") {
          return mediaItem.previewImage?.url ?? "";
        }
        return "";
      })
      .filter(Boolean) ?? []
  );
};

export const openShopifyProductPageUrl = (shopifyUrl: string) => {
  const shopName = shopifyUrl.replace(".myshopify.com", "");
  window.open(`https://admin.shopify.com/store/${shopName}/products`, "_blank");
};
